import { ChakraProvider, extendTheme, ToastProviderProps } from '@chakra-ui/react';
import { useMemo } from 'react';
import chroma from 'chroma-js';
import { WHITE, BLACK } from 'src/constants/ui';
import { getTextColor, getColorSwatch } from 'src/utils/color';
import { MarketplaceInfo, SimpleMarketplaceTheme } from 'src/api/v1-api';
import { defaultFontFamily } from 'src/utils/dynamicFonts';

const DEFAULT_FONT_FAMILY = defaultFontFamily;
const DEFAULT_LOMA_ADMIN_DARK_GREEN = '#0F4245';
const DEFAULT_LOMA_ADMIN_GREEN = '#21939A';

const DefaultThemeProvider = ({
  children,
  marketplaceProvider = null,
}: {
  children: React.ReactNode;
  marketplaceProvider?: MarketplaceInfo | SimpleMarketplaceTheme | null;
}): JSX.Element => {
  const marketplace: MarketplaceInfo | SimpleMarketplaceTheme | null = marketplaceProvider;

  const mainBG = chroma.valid(marketplace?.theme?.background_color)
    ? marketplace?.theme?.background_color
    : WHITE;
  const mainText = getTextColor(mainBG);
  const navBG = chroma.valid(marketplace?.theme?.nav_background_color)
    ? marketplace?.theme?.nav_background_color
    : mainBG;
  const navText = getTextColor(navBG);
  const borderRadius1 = marketplace?.theme?.border_radius_1 ?? '6px';
  const borderRadius2 = marketplace?.theme?.border_radius_2 ?? '4px';

  const theme = useMemo(() => {
    const overrides = {
      name: marketplace?.theme?.name,
      styles: {
        global: {
          body: {
            color: mainText,
            bgColor: mainBG,
            fontFamily: marketplace?.theme?.font_2 ?? DEFAULT_FONT_FAMILY,
          },
        },
      },
      fonts: {
        heading: marketplace?.theme?.font_1 ?? DEFAULT_FONT_FAMILY,
        body: marketplace?.theme?.font_2 ?? DEFAULT_FONT_FAMILY,
        font_1: marketplace?.theme?.font_1 ?? DEFAULT_FONT_FAMILY,
        font_2: marketplace?.theme?.font_2 ?? DEFAULT_FONT_FAMILY,
      },
      border_radius: {
        border_radius_1: `${borderRadius1}`,
        border_radius_2: `${borderRadius2}`,
      },
      colors: {
        text: getColorSwatch(mainText),
        mainBG,
        mainText,
        navBG,
        navText,
        primary: getColorSwatch(DEFAULT_LOMA_ADMIN_DARK_GREEN),
        secondary: getColorSwatch(DEFAULT_LOMA_ADMIN_GREEN),
        // Loma Branding Colors
        lomaGreen: {
          50: '#eafff5',
          100: '#ccffe5',
          200: '#9dfdd2',
          300: '#5ef7bb',
          400: '#17e098', // main color
          500: '#00cf8a',
          600: '#00a971',
          700: '#00875f',
          800: '#006b4c',
          900: '#005840',
          950: '#003225',
        },
        // technically more of a blue heavy green so use below 950 with caution
        lomaDarkGreen: {
          50: '#f1fcfa',
          100: '#d1f6f0',
          200: '#a4ebe2',
          300: '#6edacf',
          400: '#40c1b8',
          500: '#27a59f',
          600: '#1c8581',
          700: '#1b6a68',
          800: '#1a5554',
          900: '#1a4747',
          950: '#082526', // main color
        },
        lomaBlueViolet: {
          50: '#eff2ff',
          100: '#dbe2fe',
          200: '#becdff',
          300: '#92adfe',
          400: '#597efc', // main color
          500: '#3856f9',
          600: '#2235ee',
          700: '#1a22db',
          800: '#1c1eb1',
          900: '#1d218b',
          950: '#161755',
        },
      },
      components: {
        Alert: {
          baseStyle: {
            container: {
              color: BLACK,
              fontFamily: marketplace?.theme?.font_2 ?? DEFAULT_FONT_FAMILY,
              borderRadius: `${borderRadius2}`,
            },
          },
        },
        Heading: {
          baseStyle: {
            fontFamily: marketplace?.theme?.font_1 ?? DEFAULT_FONT_FAMILY,
          },
        },
        Input: {
          variants: {
            outline: {
              field: {
                bg: 'white',
                color: BLACK,
                borderRadius: `${borderRadius1}`,
                fontFamily: DEFAULT_FONT_FAMILY,
              },
            },
          },
          defaultProps: {
            variant: 'outline',
          },
        },
        NumberInput: {
          variants: {
            outline: {
              field: {
                bg: 'white',
                color: BLACK,
                borderRadius: `${borderRadius1}`,
                fontFamily: DEFAULT_FONT_FAMILY,
              },
            },
          },
          defaultProps: {
            variant: 'outline',
          },
        },
        Textarea: {
          variants: {
            outline: {
              bg: 'white',
              color: BLACK,
              borderRadius: `${borderRadius1}`,
              fontFamily: DEFAULT_FONT_FAMILY,
            },
          },
          defaultProps: {
            variant: 'outline',
          },
        },
        FormLabel: {
          baseStyle: {
            fontFamily: marketplace?.theme?.font_2 ?? DEFAULT_FONT_FAMILY,
          },
        },
        Menu: {
          baseStyle: {
            item: {
              color: BLACK,
              fontFamily: marketplace?.theme?.font_2 ?? DEFAULT_FONT_FAMILY,
            },
          },
        },
        ButtonGroup: {
          baseStyle: {
            Button: {
              color: BLACK,
              borderRadius: `${borderRadius1}`,
              fontFamily: marketplace?.theme?.font_2 ?? DEFAULT_FONT_FAMILY,
            },
          },
        },
        Button: {
          baseStyle: {
            color: BLACK,
            borderRadius: `${borderRadius1}`,
            fontFamily: marketplace?.theme?.font_2 ?? DEFAULT_FONT_FAMILY,
          },
        },
        Select: {
          variants: {
            outline: {
              field: {
                bg: 'white',
                color: BLACK,
                borderRadius: `${borderRadius1}`,
                fontFamily: marketplace?.theme?.font_2 ?? DEFAULT_FONT_FAMILY,
              },
              icon: {
                color: BLACK,
              },
            },
          },
          defaultProps: {
            variant: 'outline',
          },
        },
        Drawer: {
          baseStyle: {
            dialog: {
              color: BLACK,
              fontFamily: marketplace?.theme?.font_2 ?? DEFAULT_FONT_FAMILY,
            },
          },
        },
        Tag: {
          baseStyle: {
            container: {
              fontFamily: marketplace?.theme?.font_2 ?? DEFAULT_FONT_FAMILY,
            },
            label: {
              textTransform: 'uppercase',
            },
          },
        },
        Popover: {
          baseStyle: {
            content: {
              color: BLACK,
              borderRadius: `${borderRadius2}`,
              fontFamily: marketplace?.theme?.font_2 ?? DEFAULT_FONT_FAMILY,
            },
          },
        },
        Switch: {
          baseStyle: {
            track: {
              _checked: {
                bg: `${DEFAULT_LOMA_ADMIN_DARK_GREEN}`,
              },
            },
          },
        },
        Breadcrumb: {
          baseStyle: {
            item: {
              fontFamily: marketplace?.theme?.font_2 ?? DEFAULT_FONT_FAMILY,
            },
          },
        },
        Modal: {
          baseStyle: {
            dialog: {
              borderRadius: `${borderRadius2}`,
              bg: WHITE,
              color: BLACK,
              fontFamily: marketplace?.theme?.font_2 ?? DEFAULT_FONT_FAMILY,
            },
          },
        },
        Card: {
          baseStyle: {
            container: {
              borderRadius: `${borderRadius2}`,
            },
          },
        },
      },
    };
    return extendTheme(overrides);
    // only recompute when theme-relevant marketplace values change
  }, [
    marketplace?.theme?.name,
    marketplace?.theme?.background_color,
    marketplace?.theme?.nav_background_color,
    marketplace?.theme?.font_1,
    marketplace?.theme?.font_2,
    marketplace?.theme?.primary_color,
    marketplace?.theme?.secondary_color,
    borderRadius1,
    borderRadius2,
    mainBG,
    mainText,
    navBG,
    navText,
  ]);

  const toastOptions = useMemo(() => {
    return {
      defaultOptions: {
        containerStyle: {
          borderRadius: `${borderRadius2}`,
          fontFamily: marketplace?.theme?.font_2 ?? DEFAULT_FONT_FAMILY,
        },
      },
    } as ToastProviderProps;
  }, [borderRadius2, marketplace?.theme?.font_2]);

  return (
    <ChakraProvider theme={theme} toastOptions={toastOptions}>
      {children}
    </ChakraProvider>
  );
};

export default DefaultThemeProvider;
